import React, { useState } from 'react';
import './CitationsDisplay.css';

interface ResearchPaper {
  id: string;
  doi: string;
  summary: string;
  embedding?: number[];
  similarity_score?: number;
}

interface ResearchPapersDisplayProps {
  papers: ResearchPaper[];
  searchQuery?: string;
  className?: string;
}

const ResearchPapersDisplay: React.FC<ResearchPapersDisplayProps> = ({
  papers,
  searchQuery,
  className = ''
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!papers || papers.length === 0) return null;

  const getDOIUrl = (doi: string) => {
    if (doi.startsWith('http')) return doi;
    return `https://doi.org/${doi.replace(/_/g, '/')}`;
  };

  const PUBLISHER_MAP: Record<string, string> = {
    '10.1007': 'Springer',
    '10.1016': 'Elsevier',
    '10.1080': 'Taylor & Francis',
    '10.1371': 'PLOS',
    '10.3389': 'Frontiers',
    '10.1093': 'Oxford Academic',
    '10.1002': 'Wiley',
    '10.1111': 'Wiley',
    '10.1177': 'SAGE',
    '10.1186': 'BioMed Central',
    '10.1038': 'Nature',
    '10.1021': 'ACS',
    '10.1099': 'Microbiology Society',
    '10.1017': 'Cambridge University Press',
    '10.1152': 'APS Journals',
    '10.1292': 'J. Vet. Med. Science',
    '10.1684': 'John Libbey Eurotext',
    '10.1354': 'JAVMA',
    '10.2460': 'AJVR',
    '10.1089': 'Mary Ann Liebert',
    '10.12938': 'Veterinary World',
    '10.1155': 'Hindawi',
    '10.3390': 'MDPI',
    '10.1637': 'Avian Diseases',
    '10.7589': 'J. Wildlife Diseases',
    '10.3168': 'J. Dairy Science',
  };

  const getPublisher = (doi: string) => {
    const fixed = doi.replace(/_/g, '/');
    const prefix = fixed.match(/^(10\.\d+)/)?.[1] ?? '';
    return PUBLISHER_MAP[prefix] ?? 'doi.org';
  };

  return (
    <div className={`citations-display ${className}`}>
      <div className="citations-header">
        <div className="header-content">
          <div className="search-indicator">
            <i className="fas fa-microscope"></i>
            <span className="search-label">Relevant Research Papers</span>
            {searchQuery && (
              <span className="search-query">"{searchQuery}"</span>
            )}
          </div>
          <button
            className="expand-toggle"
            onClick={() => setExpanded(!expanded)}
          >
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`}></i>
            <span>{papers.length} paper{papers.length !== 1 ? 's' : ''}</span>
          </button>
        </div>
      </div>

      <div className={`citations-content ${expanded ? 'expanded' : 'collapsed'}`}>
        <div className="citations-grid">
          {papers.map((paper, index) => (
            <a
              key={paper.id}
              href={getDOIUrl(paper.doi)}
              target="_blank"
              rel="noopener noreferrer"
              className="citation-card citation-card--link"
            >
              <div className="citation-header">
                <div className="citation-number">[{index + 1}]</div>
                <div className="citation-source">
                  <span className="source-name">{getPublisher(paper.doi)}</span>
                  <span className="citation-ext-icon">
                    <i className="fas fa-arrow-up-right-from-square"></i>
                  </span>
                </div>
              </div>
              <div className="citation-content">
                <h4 className="citation-title">Peer-Reviewed Study</h4>
                <p className="citation-snippet">
                  {paper.summary.length > 140
                    ? `${paper.summary.substring(0, 140)}…`
                    : paper.summary}
                </p>
              </div>
              <div className="citation-footer">
                <span className="citation-url">{getDOIUrl(paper.doi)}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResearchPapersDisplay;
